//app/(app)/(root)/index.tsx
import { useCallback, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  SectionList,
  View,
  StyleSheet,
  TouchableOpacity,
  ViewToken,
  ViewabilityConfig,
  RefreshControl,
  Pressable,
} from "react-native";
import { router, useFocusEffect, usePathname, useSegments } from "expo-router";

import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { supabase } from "@/lib/supabase-client";
import useAuth from "@/hooks/useAuth";
import { ShiftType } from "@/types/db/shifts";
import { useTheme } from "@/hooks/useTheme";
import Head from "expo-router/head";
import { ModernCard, TimeChip } from "@/components/crew";

interface GroupedShift {
  title: string;
  data: ShiftType[];
}

export default function HomeScreen() {
  const [shifts, setShifts] = useState<ShiftType[]>([]);
  const [expandedSections, setExpandedSections] = useState(new Set(["Today", "Tomorrow", "This Week", "Later"]));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pathname = usePathname();
  const { user, loading: authLoading, displayNames } = useAuth();
  const segs = useSegments();
  const title = (segs as string[]).includes("crew") ? "Crew Portal" : "Home";

  const { colors } = useTheme();

  const fetchShifts = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke("get-shifts", {
        body: { role: "crew" },
      });
      if (error) throw new Error(error.message || "Failed to fetch shifts");
      const rows: ShiftType[] = Array.isArray(data) ? data : [];
      setShifts(rows);
      setErr(null);
    } catch (e: any) {
      setErr(e.message ?? `Failed to load ${displayNames?.shifts?.plural?.toLowerCase() || "shifts"}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, displayNames?.shifts?.plural]);

  useFocusEffect(
    useCallback(() => {
      if (!authLoading && user?.id) {
        fetchShifts();
      } else {
        setLoading(authLoading);
      }
    }, [authLoading, user?.id, fetchShifts]),
  );

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchShifts();
  }, [fetchShifts]);

  const me = user?.id ?? "";

  // Helper function to get user status from nested structure
  const getUserStatus = (shift: ShiftType): string | undefined => {
    const positions = shift.shift_positions || [];
    for (const pos of positions) {
      const shiftUser = pos.shift_users?.find((su) => su.id === me);
      if (shiftUser) {
        return shiftUser.status;
      }
    }
    return undefined;
  };

  const declined = useMemo(() => {
    const result = shifts.filter((s) => {
      const status = getUserStatus(s);
      return status && status === "DECLINED";
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shifts, me]);

  const remaining = useMemo(() => {
    const result = shifts
      .filter((s) => {
        const status = getUserStatus(s);
        return status && status !== "ACCEPTED" && status !== "DECLINED";
      })
      .sort((a, b) => {
        const aTime = a.start_time ? new Date(a.start_time).getTime() : 0;
        const bTime = b.start_time ? new Date(b.start_time).getTime() : 0;
        return aTime - bTime;
      });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shifts, me]);

  const toggleSection = (title: string) => {
    setExpandedSections((expandedSections) => {
      const next = new Set(expandedSections);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };

  // Group shifts by time period
  const groupedShifts = useMemo((): GroupedShift[] => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    const weekEnd = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

    const groups: GroupedShift[] = [];
    const pastShifts: ShiftType[] = [];
    const todayShifts: ShiftType[] = [];
    const tomorrowShifts: ShiftType[] = [];
    const thisWeekShifts: ShiftType[] = [];
    const laterShifts: ShiftType[] = [];

    remaining.forEach((shift) => {
      if (!shift.start_time) {
        laterShifts.push(shift);
        return;
      }
      const startDate = new Date(shift.start_time);
      if (startDate < today) {
        pastShifts.push(shift);
      } else if (startDate < tomorrow) {
        todayShifts.push(shift);
      } else if (startDate < new Date(tomorrow.getTime() + 24 * 60 * 60 * 1000)) {
        tomorrowShifts.push(shift);
      } else if (startDate < weekEnd) {
        thisWeekShifts.push(shift);
      } else {
        laterShifts.push(shift);
      }
    });

    if (todayShifts.length > 0) groups.push({ title: "Today", data: todayShifts });
    if (tomorrowShifts.length > 0) groups.push({ title: "Tomorrow", data: tomorrowShifts });
    if (thisWeekShifts.length > 0) groups.push({ title: "This Week", data: thisWeekShifts });
    if (laterShifts.length > 0) groups.push({ title: "Later", data: laterShifts });
    if (declined.length > 0) groups.push({ title: "Declined", data: declined });
    if (pastShifts.length > 0) groups.push({ title: "Previous", data: pastShifts });

    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining]);

  // Track viewed shifts to only log once per shift
  const viewedShiftsRef = useRef<Set<string>>(new Set());

  const onViewableItemsChanged = useRef(({ viewableItems }: { viewableItems: ViewToken[] }) => {
    viewableItems.forEach((v) => {
      if (v.isViewable && v.item?.id && me) {
        // Only update once per shift per session
        if (!viewedShiftsRef.current.has(v.item.id)) {
          viewedShiftsRef.current.add(v.item.id);
          // Update meta_data with viewed: true via edge function
          supabase.functions.invoke("respond-shift", {
            body: { shiftId: v.item.id, action: "update_meta", meta: { viewed: true } },
          });
        }
      }
    });
  }).current;

  // Viewability config for "solid viewing" - 80% visible for 3000ms
  const viewabilityConfig = useRef<ViewabilityConfig>({
    itemVisiblePercentThreshold: 80,
    minimumViewTime: 3000,
  }).current;

  // Get venue type (shift-level or project fallback)
  const getVenueType = (shift: ShiftType): string | null => {
    const shiftVenue = (shift.meta_data as any)?.venue_type;
    if (shiftVenue && shiftVenue !== "Unspecified") return shiftVenue;
    const projectVenue = (shift.project as any)?.meta_data?.venue_type;
    if (projectVenue && projectVenue !== "Unspecified") return projectVenue;
    return null;
  };

  const renderShiftCard = ({ item: shift }: { item: ShiftType }) => {
    // Calculate shift info
    const startTime = shift.start_time ? new Date(shift.start_time) : null;
    const endTime = shift.end_time ? new Date(shift.end_time) : null;
    const duration =
      startTime && endTime ? Math.round(((endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60)) * 10) / 10 : 0;

    // Get user's position info
    const myPosition = shift.shift_positions?.find((pos) => pos.shift_users?.some((su) => su.id === me));

    // Calculate earnings
    const payrollRate = (myPosition as any)?.payroll_rate?.rate || 0;
    const giftedHours = (myPosition as any)?.gifted_hours || 1;
    const minEarnings = giftedHours * payrollRate;
    const maxEarnings = duration * payrollRate;

    // Get venue type
    const venueType = getVenueType(shift);
    const inviteStatus = getUserStatus(shift);

    return (
      <View style={s.timelineItem}>
        {/* Timeline connector */}
        <View style={[s.timelineConnector, { backgroundColor: colors.border }]} />

        {/* Timeline dot */}
        <View style={[s.timelineDot, { backgroundColor: colors.primary }]} />

        {/* Card */}
        <TouchableOpacity
          onPress={() =>
            router.push({
              pathname: `${pathname.includes("/crew/") ? "crew/home" : "home"}/${shift.id}` as any,
              params: { context: "invited" },
            })
          }
          activeOpacity={0.7}
          style={s.cardTouchable}
        >
          <ModernCard style={s.modernShiftCard}>
            {/* Header */}
            <View style={s.cardHeader}>
              <View style={{ flex: 1 }}>
                <ThemedText style={s.shiftName} numberOfLines={1}>
                  {shift.name || `Untitled ${displayNames?.shifts?.name || "Shift"}`}
                  {"  "}
                  <ThemedText style={[s.shiftId, { color: colors.placeholder }]}>
                    {shift.id?.slice(0, 6).toUpperCase()}
                  </ThemedText>
                </ThemedText>
                {(shift.project?.client?.name || shift.project?.name) && (
                  <ThemedText style={[s.projectName, { color: colors.textSecondary }]} numberOfLines={1}>
                    {[shift.project?.client?.name, shift.project?.name].filter(Boolean).join(" · ")}
                  </ThemedText>
                )}
                {myPosition?.position?.name && (
                  <ThemedText style={[s.positionName, { color: colors.primary }]} numberOfLines={1}>
                    {myPosition.position.name}
                  </ThemedText>
                )}
                {(() => {
                  const loc = shift.location || shift.project?.location;
                  if (!loc?.name) return null;
                  const addr = [loc.address?.city, loc.address?.state].filter(Boolean).join(", ");
                  return (
                    <ThemedText style={[s.projectName, { color: colors.textSecondary }]} numberOfLines={1}>
                      {addr ? `${loc.name} · ${addr}` : loc.name}
                    </ThemedText>
                  );
                })()}
              </View>
            </View>

            {/* Details chips */}
            <View style={s.cardDetails}>
              {/* Date chip */}
              {startTime && (
                <TimeChip
                  type="status"
                  text={startTime.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}
                  size="small"
                />
              )}

              {/* Time chip */}
              {startTime && (
                <TimeChip
                  type="time"
                  text={startTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  size="small"
                />
              )}

              {/* Duration chip */}
              {duration > 0 && <TimeChip type="duration" text={`~${duration}h`} size="small" />}

              {/* Rate chip */}
              {payrollRate > 0 && <TimeChip type="status" text={`$${payrollRate}/hr`} size="small" />}

              {/* Earnings chip */}
              {payrollRate > 0 && (
                <TimeChip
                  type="earnings"
                  text={
                    minEarnings === maxEarnings
                      ? `$${minEarnings.toFixed(0)}`
                      : `$${minEarnings.toFixed(0)} - $${maxEarnings.toFixed(0)}`
                  }
                  size="small"
                />
              )}

              {/* Venue chip */}
              {venueType && <TimeChip type="status" text={venueType} size="small" />}
              {inviteStatus === "DECLINED" && <TimeChip type="alert" text="Declined" size="small" />}
            </View>
          </ModernCard>
        </TouchableOpacity>
      </View>
    );
  };

  const ListFooter = () => (
    <View style={s.listFooter}>
      <MaterialCommunityIcons name="check-circle-outline" size={24} color={colors.placeholder} />
      <ThemedText style={[s.footerText, { color: colors.textSecondary }]}>You&apos;re all caught up</ThemedText>
    </View>
  );

  if (loading) {
    return (
      <View style={[s.container, s.center, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
        <ThemedText style={{ marginTop: 12, opacity: 0.6 }}>
          Loading your {displayNames?.shifts?.plural?.toLowerCase() || "shifts"}...
        </ThemedText>
      </View>
    );
  }

  if (err) {
    return (
      <View style={[s.container, s.center, { backgroundColor: colors.background }]}>
        <MaterialCommunityIcons name="alert-circle-outline" size={48} color={colors.red.border} />
        <ThemedText style={{ marginTop: 12, fontSize: 16 }}>{err}</ThemedText>
      </View>
    );
  }

  if (shifts.length === 0) {
    return (
      <View style={[s.container, s.center, { backgroundColor: colors.background }]}>
        <MaterialCommunityIcons name="calendar-blank-outline" size={64} color={colors.placeholder} />
        <ThemedText style={s.emptyTitle}>No {displayNames?.shifts?.plural || "Shifts"} Available</ThemedText>
        <ThemedText style={[s.emptySubtitle, { color: colors.textSecondary }]}>
          Your {displayNames?.shifts?.name?.toLowerCase() || "shift"} invitations will appear here
        </ThemedText>
      </View>
    );
  }

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content="" />
      </Head>
      <View style={[s.container, { backgroundColor: colors.background }]}>
        <SectionList
          sections={groupedShifts}
          onViewableItemsChanged={onViewableItemsChanged}
          viewabilityConfig={viewabilityConfig}
          extraData={expandedSections}
          keyExtractor={(item, index) => item.id ?? `shift-${index}`}
          renderItem={({ section: { title }, item }) => {
            if (!expandedSections.has(title)) return null;
            return renderShiftCard({ item });
          }}
          renderSectionHeader={({ section }) => (
            <View style={[s.sectionHeader, { backgroundColor: colors.background }]}>
              <Pressable onPress={() => toggleSection(section.title)}>
                <ThemedText style={[s.sectionTitle, { color: colors.text }]}>
                  <TimeChip
                    type="duration"
                    style={{ minWidth: 50, justifyContent: "center" }}
                    text={section.data.length.toString()}
                    size="small"
                  />{" "}
                  {section.title}
                  <MaterialCommunityIcons
                    name={expandedSections.has(section.title) ? "chevron-down" : "chevron-up"}
                    size={18}
                    color={colors.placeholder}
                  />
                </ThemedText>
              </Pressable>
            </View>
          )}
          ListFooterComponent={remaining.length > 0 ? ListFooter : null}
          contentContainerStyle={s.list}
          showsVerticalScrollIndicator={false}
          stickySectionHeadersEnabled={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
        />
      </View>
    </>
  );
}

/* ---------- styles ---------- */

const s = StyleSheet.create({
  container: {
    paddingTop: 40,
    flex: 1,
  },
  list: {
    paddingBottom: 32,
  },
  sectionHeader: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 8,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: "600",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  center: { alignItems: "center", justifyContent: "center", padding: 24 },
  emptyTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginTop: 16,
  },
  emptySubtitle: {
    fontSize: 14,
    marginTop: 4,
  },
  listFooter: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 32,
    gap: 8,
  },
  footerText: {
    fontSize: 14,
  },

  // Timeline & Cards
  timelineItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingHorizontal: 16,
    marginBottom: 16,
    position: "relative",
  },
  timelineConnector: {
    position: "absolute",
    left: 27,
    top: 20,
    bottom: -16,
    width: 2,
    opacity: 0.3,
  },
  timelineDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginTop: 8,
    marginRight: 16,
    zIndex: 1,
  },
  cardTouchable: {
    flex: 1,
  },
  modernShiftCard: {
    margin: 0,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 12,
    gap: 8,
  },
  shiftName: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 2,
  },
  shiftId: {
    fontSize: 12,
    fontWeight: "400",
  },
  projectName: {
    fontSize: 13,
  },
  positionName: {
    fontSize: 12,
    fontWeight: "500",
    marginTop: 4,
  },
  cardDetails: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
});
